import { animate, style, transition, trigger } from '@angular/animations';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnInit, Output, PLATFORM_ID, QueryList, ViewChildren } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { Subject, debounceTime } from 'rxjs';
import { FasteignService } from 'src/app/components/services/api/fasteign/fasteign.service';
import { NotandiService } from 'src/app/components/services/api/notandi/notandi.service';
import { formatPriceString, thousandSeparator } from 'src/app/helpers/thousandSeparator';
import { AccountsLoginRes } from 'src/app/interfaces/CRUDL/read/AccountsLoginRes';
import { ListPostnumerRes } from 'src/app/interfaces/responses/List/listPostnumerRes';
import { environment } from 'src/environments/environment';
import { AppService } from '../../app.service';

type Group = { svaedi: string; items: ListPostnumerRes[] };

@Component({
  selector: 'app-properties-search',
  templateUrl: './properties-search.component.html',
  styleUrls: ['./properties-search.component.scss'],
  animations: [
    trigger('fadeSlide', [
      // birtist mjúklega
      transition(':enter', [
        style({ opacity: 0, height: 0, overflow: 'hidden', transform: 'translateY(-8px)' }),
        animate('220ms ease-out', style({ opacity: 1, height: '*', transform: 'translateY(0)' })),
      ]),
      // hverfur mjúklega áður en það er fjarlægt úr DOM
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('220ms ease-in', style({ opacity: 0, height: 0, transform: 'translateY(-8px)' })),
      ]),
    ]),
  ]
})
export class PropertiesSearchComponent implements OnInit {

  //#region Globals

  private inputSubjects: { [key: string]: Subject<string> } = {};

  public isBrowser: boolean;
  public isServer: boolean;

  userDetails: AccountsLoginRes;

  @Input() localStorageFilterName: string;
  @Input() kaupsagan: boolean = false;
  @Input() tilSolu: boolean = false;
  @Input() showTilSoluCheckboxes: boolean = true;
  @Input() variant: number = 1;
  @Input() vertical: boolean = false;

  @Output() onSearchClick: EventEmitter<any> = new EventEmitter<any>();
  @Input() _formFilters: UntypedFormGroup;
  @Input() _formMapPointFilters: UntypedFormGroup;

  // Listi yfir hluti sem hægt er að nota í filtering.
  // Sótt frá vefþjónustu.
  public postnumer: Array<ListPostnumerRes>;

  verdOptions: Array<number> = [
    10,
    20,
    30,
    40,
    50,
    60,
    70,
    80,
    90,
    100,
    110,
    120,
    130,
    140,
    150,
    160,
    170,
    180,
    190,
    200,
    250,
    500,
    750,
    1000
  ];

  staerdOptions: Array<number> = [
    25,
    50,
    75,
    100,
    125,
    150,
    175,
    200,
    250,
    350,
    500,
    750,
    1000
  ]

  //#endregion

  //#region Constructor and init

  constructor(public appService: AppService, public fb: UntypedFormBuilder, public dialog: MatDialog, public fasteignService: FasteignService, @Inject(PLATFORM_ID) platformId: Object, private notandiService: NotandiService) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.isServer = isPlatformServer(platformId);

    if (this.isBrowser) {
      try {
        this.notandiService.userDetails.subscribe(userDetails => this.userDetails = userDetails);
      } catch { }
    }
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.saekjaSvaedi();
      this.updateActiveFilterLabels();
      this._formFilters?.valueChanges?.subscribe(() => {
        this.updateActiveFilterLabels();
        this._formMapPointFilters.reset();
      });
    }
  }

  //#endregion

  //#region Search functions

  flytimerktarSearch() {
    if (this.userDetails) {
      this.search();
    } else {
      window.location.href = environment.portalUrl + "/fasteignir/til-solu/syna-adeins-flytimerktar";
    }
  }

  public search() {
    this.updateActiveFilterLabels();
    this.onSearchClick.emit(this._formFilters);
  }

  // Ef notast er við textaleit þá er alltaf beiðið í hálfa sekúndu frá því notandinn
  // sló inn, til að fyrirbyggja of margar leitir.
  onSearchInput(event: Event, inputElement: HTMLInputElement): void {
    const inputId = inputElement.id || inputElement.name || 'default';
    if (!this.inputSubjects[inputId]) {
      this.inputSubjects[inputId] = new Subject<string>();
      this.inputSubjects[inputId]
        .pipe(debounceTime(700))
        .subscribe((value) => {
          this.search();
        });
    }
    this.inputSubjects[inputId].next(inputElement.value);
  }

  //#endregion

  //#region Svæði filter

  groupedPostnumer: Group[] = [];
  selectedIds = new Set<number>(); // geymir valin numer

  // --- Data sækja & hópun ---
  saekjaSvaedi() {
    this.fasteignService.listPostnumer().subscribe(res => {
      this.postnumer = res.filter(
        x =>
          x.tegund != 'Pósthólf' &&
          // Engar eignir eru til staðar undir þessum póstnúmerum (sjá staðfangalista):
          x.numer != 150 &&
          x.numer != 155
      ) ?? [];

      // Hópun (óbreytt)
      const map = new Map<string, ListPostnumerRes[]>();
      for (const p of this.postnumer) {
        if (!map.has(p.svaedi)) map.set(p.svaedi, []);
        map.get(p.svaedi)!.push(p);
      }
      this.groupedPostnumer = Array.from(map, ([svaedi, items]) => ({
        svaedi,
        items: items.sort((a, b) => (a.numer ?? 0) - (b.numer ?? 0)),
      }));

      // Halda selectedIds í samræmi við tiltæk póstnúmer
      const validIds = new Set(this.postnumer.map(p => p.numer));
      for (const id of Array.from(this.selectedIds)) {
        if (!validIds.has(id)) this.selectedIds.delete(id);
      }

      // LESA forstillt gildi úr formi (t.d. úr localStorage endurhleðslu)
      const initial = (this._formFilters.get('region')?.value ?? []) as number[];
      if (Array.isArray(initial) && initial.length) {
        // setja aðeins þau sem eru til
        for (const id of initial) {
          if (validIds.has(id)) this.selectedIds.add(id);
        }
      }

      // Loks spegla yfir í formið (þannig formið og UI séu í sync)
      this.syncFormRegionFromSelectedIds();
      this.updateActiveFilterLabels();
    });
  }

  // --- Valdar færslur (fyrir label) ---
  get valinPostnumer(): ListPostnumerRes[] {
    const m = new Map(this.postnumer.map(p => [p.numer, p] as const));
    return Array.from(this.selectedIds)
      .map(id => m.get(id)!)
      .filter(Boolean);
  }

  // --- Label í input ---
  get displayLabel(): string {
    // Ef engin gögn hlaðin
    if (!this.postnumer?.length) return 'Allt landið';

    // Ef ekkert valið eða allt valið
    if (this.selectedIds.size === 0 || this.selectedIds.size === this.postnumer.length) {
      return 'Allt landið';
    }

    // Annars sýna valin póstnúmer
    const items = this.valinPostnumer.map(
      p => `${p.numer} - ${(p.heitiNf ?? p.heiti)}`
    );

    if (items.length <= 2) return items.join(', ');
    return `${items.slice(0, 2).join(', ')} +${items.length - 2}`;
  }

  private syncFormRegionFromSelectedIds() {
    const ids = this.selectedIdsArray; // Array<number>
    this._formFilters.get('region')?.setValue(ids, { emitEvent: false });
    try {
      localStorage.setItem(this.localStorageFilterName, JSON.stringify(this._formFilters.value));
    } catch { }
  }

  // --- Hak á stökum póstnúmerum ---
  toggleItem(p: ListPostnumerRes, checked: boolean) {
    if (checked) this.selectedIds.add(p.numer);
    else this.selectedIds.delete(p.numer);
    this.syncFormRegionFromSelectedIds();
    this.search();
  }

  // --- Hak á heilu svæði (velja/afv elja allt) ---
  toggleGroup(group: Group, checked: boolean) {
    if (checked) {
      for (const p of group.items) this.selectedIds.add(p.numer);
    } else {
      for (const p of group.items) this.selectedIds.delete(p.numer);
    }
    this.syncFormRegionFromSelectedIds();
    this.search();
  }

  // --- Staða svæðis-haks (til að sýna checked/indeterminate á header) ---
  isGroupAllSelected(group: Group): boolean {
    if (!group.items.length) return false;
    return group.items.every(p => this.selectedIds.has(p.numer));
  }

  isGroupIndeterminate(group: Group): boolean {
    const any = group.items.some(p => this.selectedIds.has(p.numer));
    return any && !this.isGroupAllSelected(group);
  }

  // --- Chips toggling (ef þú ert með chips annars staðar) ---
  togglePostnumer(p: ListPostnumerRes) {
    if (this.selectedIds.has(p.numer)) this.selectedIds.delete(p.numer);
    else this.selectedIds.add(p.numer);
    this.syncFormRegionFromSelectedIds();
    this.search();
  }

  // --- Reactive Forms tenging ef þarf ---
  get selectedIdsArray(): number[] {
    return Array.from(this.selectedIds);
  }

  // --- Expansion panels: loka við opnun valmyndar ---
  @ViewChildren(MatExpansionPanel) panels!: QueryList<MatExpansionPanel>;
  collapseAll() {
    setTimeout(() => this.panels?.forEach(p => p.close()));
  }

  //#endregion

  //#region Active filter labels

  activeFilters: any[] = [];

  // Hjálparfall: finna heiti póstnúmers út frá numer
  private getPostnumerLabel(numer: number | string): string {
    for (const group of this.groupedPostnumer ?? []) {
      const found = group.items?.find((p: any) => String(p.numer) === String(numer));
      if (found) return `${found.numer} - ${found.heitiNf ?? found.heiti}`;
    }
    return String(numer);
  }

  // Hjálparfall: skila völdum póstnúmerum sem lista (úr this.selectedIds: Set<number>)
  private getSelectedPostnumerList(): Array<number | string> {
    return Array.from(this.selectedIds ?? []);
  }

  // Kalla þegar form breytist / toggles breytast / póstnúmer veljast
  private updateActiveFilterLabels(): void {
    const filters: any[] = [];

    // 1) Textaleit
    const searchText = this._formFilters?.get('searchText')?.value?.trim();
    if (searchText) {
      filters.push({
        key: 'searchText',
        label: `Textaleit: „${searchText}“`,
        remove: () => {
          this._formFilters.get('searchText')?.setValue('');
          this.search();
          this.updateActiveFilterLabels();
        }
      });
    }

    // 2) Póstnúmer (eitt chip per póstnúmer fyrir skýra stjórn)
    const selected = this.getSelectedPostnumerList();
    if (selected.length > 0) {
      const firstLbl = this.getPostnumerLabel(selected[0]);
      const extraCount = selected.length - 1;
      const label =
        extraCount > 0
          ? `Svæði / Póstnúmer: ${firstLbl} (+${extraCount} til viðbótar)`
          : `Svæði / Póstnúmer: ${firstLbl}`;

      filters.push({
        key: 'postnumer',
        label,
        remove: () => {
          this.selectedIds.clear(); // hreinsa allt
          this._formFilters.get('region')?.setValue([]);
          this.search();
          this.updateActiveFilterLabels();
        }
      });
    }

    if (this.tilSolu || (this.kaupsagan && this._formFilters.get("kort").value)) {
      const priceFrom = this._formFilters?.get('price.from')?.value;
      const priceTo = this._formFilters?.get('price.to')?.value;
      if (priceFrom) {
        filters.push({
          key: 'price:from',
          label: `Verð frá: ${this.formatPriceString(priceFrom)} m.kr.`,
          remove: () => {
            this._formFilters.get('price.from')?.setValue('');
            this.search();
            this.updateActiveFilterLabels();
          }
        });
      }
      if (priceTo) {
        filters.push({
          key: 'price:to',
          label: `Verð til: ${this.formatPriceString(priceTo)} m.kr.`,
          remove: () => {
            this._formFilters.get('price.to')?.setValue('');
            this.search();
            this.updateActiveFilterLabels();
          }
        });
      }

      // 4) Stærð
      const sizeFrom = this._formFilters?.get('size.from')?.value;
      const sizeTo = this._formFilters?.get('size.to')?.value;
      if (sizeFrom) {
        filters.push({
          key: 'size:from',
          label: `Stærð frá: ${this.sizeString(sizeFrom)} m²`,
          remove: () => {
            this._formFilters.get('size.from')?.setValue('');
            this.search();
            this.updateActiveFilterLabels();
          }
        });
      }
      if (sizeTo) {
        filters.push({
          key: 'size:to',
          label: `Stærð til: ${this.sizeString(sizeTo)} m²`,
          remove: () => {
            this._formFilters.get('size.to')?.setValue('');
            this.search();
            this.updateActiveFilterLabels();
          }
        });
      }
    }

    if (!this._formFilters?.get('kort')?.value && this.tilSolu && this.showTilSoluCheckboxes) {
      if (this._formFilters?.get('tilSoluEfasteignir')?.value) {
        filters.push({
          key: 'tilSoluEfasteignir',
          label: 'Til sölu hjá e-fasteignum',
          remove: () => {
            this._formFilters.get('tilSoluEfasteignir')?.setValue(false);
            this.search();
            this.updateActiveFilterLabels();
          }
        });
      }

      if (this._formFilters?.get('tilSoluMbl')?.value) {
        filters.push({
          key: 'tilSoluMbl',
          label: 'Til sölu hjá mbl.is',
          remove: () => {
            this._formFilters.get('tilSoluMbl')?.setValue(false);
            this.search();
            this.updateActiveFilterLabels();
          }
        });
      }
    }

    this.activeFilters = filters;
  }

  // #endregion

  //#region Helper methods

  clearSizeField(controlName: 'from' | 'to'): void {
    const sizeGroup = this._formFilters.get('size');
    if (sizeGroup) {
      sizeGroup.get(controlName)?.setValue(null);
      this.search();
    }
  }

  clearPriceFieldProperty(controlName: 'from' | 'to'): void {
    const priceGroup = this._formFilters.get('price');
    if (priceGroup) {
      priceGroup.get(controlName)?.setValue(null);
      this.search();
    }
  }

  formatPriceString(s) {
    return formatPriceString(s.toString());
  }

  sizeString(s) {
    return thousandSeparator(s.toString());
  }

  public getAppearance() {
    return (this.variant != 3) ? 'outline' : '';
  }

  public getFloatLabel() {
    return (this.variant == 1) ? 'always' : '';
  }

  //#endregion

}
